// Comment and suggestion anchors (FragmentDocument.currentRange / currentSynopsisRange): (anchorStart, anchorEnd) in
// Unicode scalars of the text at `anchorHeads`. JS Automerge counts UTF-16, so every position crosses offsets.ts.
import * as A from '@automerge/automerge';
import { decodeHeadsIn } from './heads.ts';
import { scalarOffsetOfUTF16, sliceScalars, utf16OffsetOfScalar } from './offsets.ts';
import { ROOT, SwiftReader } from './swiftDoc.ts';
import { occurrences } from '../quoteSearch.ts';

export interface Anchor { anchorStart: number; anchorEnd: number; anchorHeads: string }

function textAt(doc: A.Doc<unknown>, key: string, heads?: string[]): { id: string; text: string } | undefined {
  const r = new SwiftReader(doc, heads);
  const v = r.get(ROOT, key);
  if (!v || v[0] !== 'text') return undefined;
  return { id: v[1] as string, text: r.text(v[1] as string) };
}

/** Where an anchored passage is now, in scalars of the current text (`key` is `text`, or `synopsis` for synopsis
 *  comments); null when the passage was deleted or the anchor's heads aren't in this document (not synced yet). */
export function currentRange(doc: A.Doc<unknown>, anchor: Anchor, key = 'text'): [number, number] | null {
  const heads = decodeHeadsIn(anchor.anchorHeads, doc);
  if (!heads) return null;
  const then = textAt(doc, key, heads);
  const now = textAt(doc, key);
  if (!then || !now) return null;
  const b = A.getBackend(doc);
  const u16 = (s: number) => utf16OffsetOfScalar(then.text, s);
  try {
    const start = b.getCursorPosition(now.id, b.getCursor(then.id, u16(anchor.anchorStart), heads));
    const end = b.getCursorPosition(now.id, b.getCursor(then.id, u16(anchor.anchorEnd), heads));
    const s = scalarOffsetOfUTF16(now.text, start), e = scalarOffsetOfUTF16(now.text, end);
    return e > s ? [s, e] : null;
  } catch {
    return null;
  }
}

/** The same range in UTF-16 units of the current text, for an editor or a reader. */
export function currentRangeUTF16(doc: A.Doc<unknown>, anchor: Anchor, key = 'text'): [number, number] | null {
  const range = currentRange(doc, anchor, key);
  const now = textAt(doc, key);
  if (!range || !now) return null;
  return [utf16OffsetOfScalar(now.text, range[0]), utf16OffsetOfScalar(now.text, range[1])];
}

/** The passage an anchor names, read at its heads (what `quote` was when the comment was made). */
export function anchoredText(doc: A.Doc<unknown>, anchor: Anchor, key = 'text'): string | null {
  const heads = decodeHeadsIn(anchor.anchorHeads, doc);
  const then = heads ? textAt(doc, key, heads) : undefined;
  return then ? sliceScalars(then.text, anchor.anchorStart, anchor.anchorEnd) : null;
}

/** `currentRange(of: Suggestion)`: as `currentRange`, except that an insertion (an empty anchor) maps its point, so it
 *  comes back as an empty range rather than null. */
export function suggestionRange(doc: A.Doc<unknown>, anchor: Anchor): [number, number] | null {
  if (anchor.anchorStart !== anchor.anchorEnd) return currentRange(doc, anchor);
  const heads = decodeHeadsIn(anchor.anchorHeads, doc);
  if (!heads) return null;
  const then = textAt(doc, 'text', heads);
  const now = textAt(doc, 'text');
  if (!then || !now) return null;
  const b = A.getBackend(doc);
  try {
    const p = scalarOffsetOfUTF16(now.text, b.getCursorPosition(now.id, b.getCursor(then.id, utf16OffsetOfScalar(then.text, anchor.anchorStart), heads)));
    return [p, p];
  } catch {
    return null;
  }
}

/** `currentSynopsisRange(of:)`: a synopsis comment's passage followed through edits by its anchor, else found again by
 *  its quote (after compaction, or a synopsis rewritten whole). Null for a comment on the whole synopsis (no quote), or
 *  a passage that's gone. */
export function currentSynopsisRange(doc: A.Doc<unknown>, comment: Anchor & { quote: string }): [number, number] | null {
  if (!comment.quote) return null;
  const range = currentRange(doc, comment, 'synopsis');
  if (range) return range;
  return occurrences(comment.quote, new SwiftReader(doc).string(ROOT, 'synopsis') ?? '')[0] ?? null;
}
